import crypto from 'crypto-js';

import type { StringIndexedObject } from '~/types';
import { FRONT_USER_HASH } from '~/config';

export interface FrontChatWidgetSettings {
  email: string;
  name?: string;
  customFields?: StringIndexedObject;
}

export interface FrontChatWidgetHook {
  openChat: () => void;
  closeChat: () => void;
  update: (settings: FrontChatWidgetSettings) => void;
}

/**
 * Hook for using FrontChat Javascript API Methods.
 *
 * Docs:
 * - Methods:
 *   https://dev.frontapp.com/docs/chat-sdk-reference
 */
export default function useFrontChatWidget(): FrontChatWidgetHook {
  const openChat = () => {
    // If FrontChat isn't available, return
    if (!window.FrontChat) return;
    // show chat window
    window.FrontChat('show');
  };

  const closeChat = () => {
    // If FrontChat isn't available, return
    if (!window.FrontChat) return;
    // hide chat window
    window.FrontChat('hide');
  };

  /**
   * Function calls the FrontChat update method
   * @param {Object} - Settings/Data (FrontChat setings) to update FrontChat with,
   * allowed attributes contained in Attributes & Objects docs link above
   */
  const update = (settings: FrontChatWidgetSettings) => {
    // If FrontChat isn't available, return
    const { email } = settings;
    if (!window.FrontChat) return;
    // Check the settings passed is an object - if so, run the update method
    if (
      typeof settings === 'object' &&
      settings !== null &&
      !Array.isArray(settings)
    ) {
      const userHash = crypto
        .HmacSHA256(email, FRONT_USER_HASH)
        .toString(crypto.enc.Hex);
      const payload = {
        ...settings,
        userHash,
      };
      window.FrontChat('identity', payload);
    }
  };

  return {
    openChat,
    closeChat,
    update,
  };
}
